// QADialog.js - QA Check Results Dialog with Actionable Issues
import React, { useState } from 'react';
import { X, CheckCircle, AlertTriangle, Loader, ChevronDown, ChevronUp, Edit2 } from 'lucide-react';

const QADialog = ({ isOpen, onClose, qaResults, qaType, isLoading, onApplyChanges }) => {
  const [expandedIssues, setExpandedIssues] = useState({});
  const [editingIssue, setEditingIssue] = useState(null);
  const [editText, setEditText] = useState('');
  const [selectedActions, setSelectedActions] = useState({});

  if (!isOpen) return null;

  const toggleExpanded = (issueId) => {
    setExpandedIssues(prev => ({
      ...prev,
      [issueId]: !prev[issueId]
    }));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-50 border-red-200 text-red-800';
      case 'medium': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'low': return 'bg-blue-50 border-blue-200 text-blue-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'high': return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">HIGH</span>;
      case 'medium': return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">MEDIUM</span>;
      case 'low': return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">LOW</span>;
      default: return null;
    }
  };

  const getIcon = () => {
    if (qaType === 'guide') {
      return <CheckCircle className="w-6 h-6 text-blue-500" />;
    } else {
      return <AlertTriangle className="w-6 h-6 text-purple-500" />;
    }
  };

  const getTitle = () => {
    if (qaType === 'guide') {
      return 'Guide Consistency Check';
    } else {
      return 'Museum Criticism Check';
    }
  };

  const handleEditStart = (issue) => {
    setEditingIssue(issue.id);
    setEditText(issue.suggestedText);
  };

  const handleAccept = (issue) => {
    setSelectedActions(prev => ({
      ...prev,
      [issue.id]: {
        action: 'accept',
        text: editingIssue === issue.id ? editText : issue.suggestedText
      }
    }));
  };

  const handleIgnore = (issue) => {
    setSelectedActions(prev => ({
      ...prev,
      [issue.id]: { action: 'ignore' }
    }));
  };

  const handleApplyAll = () => {
    if (onApplyChanges) {
      onApplyChanges(selectedActions, qaResults.issues);
    }
  };

  const acceptedCount = Object.values(selectedActions).filter(a => a.action === 'accept').length;
  const ignoredCount = Object.values(selectedActions).filter(a => a.action === 'ignore').length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {getIcon()}
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{getTitle()}</h2>
              {!isLoading && qaResults && (
                <p className="text-sm text-gray-500 mt-1">{qaResults.summary}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="w-12 h-12 animate-spin text-blue-500 mb-4" />
              <p className="text-lg text-gray-700">
                Analyzing {qaResults?.totalPois || 0} points of interest...
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This may take a few moments
              </p>
            </div>
          )}

          {!isLoading && qaResults && (
            <div className="space-y-4">
              {qaResults.parseError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-semibold">⚠️ Parse Error</p>
                  <p className="text-sm text-red-700 mt-2">
                    Failed to parse AI response. Raw response displayed below:
                  </p>
                  <pre className="mt-3 p-3 bg-white border border-red-200 rounded text-xs overflow-auto max-h-40">
                    {qaResults.rawResponse}
                  </pre>
                </div>
              )}

              {qaResults.noIssuesFound ? (
                <div className="p-6 bg-green-50 border border-green-200 rounded-lg text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-lg font-semibold text-green-800">No Issues Found! ✅</p>
                  <p className="text-green-700 mt-2">Your guide content looks great.</p>
                </div>
              ) : qaResults.issues && qaResults.issues.length > 0 ? (
                <>
                  {qaResults.issues.map((issue) => (
                    <div key={issue.id} className={`border-2 rounded-lg ${getSeverityColor(issue.severity)}`}>
                      {/* Issue Header */}
                      <div
                        className="p-4 cursor-pointer flex items-center justify-between hover:bg-opacity-75 transition-colors"
                        onClick={() => toggleExpanded(issue.id)}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {expandedIssues[issue.id] ? (
                            <ChevronUp className="w-5 h-5 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold capitalize">{issue.type.replace(/_/g, ' ')}</span>
                              {getSeverityBadge(issue.severity)}
                              {issue.field && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium">
                                  {issue.field === 'inDepthDescription' ? 'In-Depth' : 'Short'} Description
                                </span>
                              )}
                            </div>
                            <p className="text-sm mt-1">{issue.problem}</p>
                            <p className="text-xs mt-1 font-medium">
                              Affects: {issue.affectedPois.join(', ')}
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-4">
                          {selectedActions[issue.id]?.action === 'accept' && (
                            <span className="px-3 py-1 bg-green-200 text-green-800 rounded text-xs font-semibold">✓ ACCEPTED</span>
                          )}
                          {selectedActions[issue.id]?.action === 'ignore' && (
                            <span className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-xs font-semibold">✗ IGNORED</span>
                          )}
                        </div>
                      </div>

                      {/* Issue Details */}
                      {expandedIssues[issue.id] && (
                        <div className="px-4 pb-4 border-t border-current border-opacity-20 space-y-3">
                          {/* Explanation */}
                          <div>
                            <p className="text-xs font-semibold uppercase opacity-75 mb-1">Why This Matters</p>
                            <p className="text-sm">{issue.explanation}</p>
                          </div>

                          {/* Current Text */}
                          <div>
                            <p className="text-xs font-semibold uppercase opacity-75 mb-1">Current Text</p>
                            <div className="p-3 bg-white bg-opacity-50 rounded border border-current border-opacity-20 font-mono text-sm">
                              {issue.currentText}
                            </div>
                          </div>

                          {/* Suggested Text (or Editable) */}
                          <div>
                            <p className="text-xs font-semibold uppercase opacity-75 mb-1">Suggested Text</p>
                            {editingIssue === issue.id ? (
                              <textarea
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                className="w-full p-3 border border-current border-opacity-30 rounded font-mono text-sm"
                                rows="4"
                              />
                            ) : (
                              <div className="p-3 bg-white bg-opacity-50 rounded border border-current border-opacity-20 font-mono text-sm">
                                {issue.suggestedText}
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 pt-2">
                            {editingIssue === issue.id ? (
                              <>
                                <button
                                  onClick={() => {
                                    handleAccept(issue);
                                    setEditingIssue(null);
                                  }}
                                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors font-medium text-sm"
                                >
                                  ✓ Accept Edit
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingIssue(null);
                                    setEditText('');
                                  }}
                                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors font-medium text-sm"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleAccept(issue)}
                                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors font-medium text-sm"
                                >
                                  ✓ Accept
                                </button>
                                <button
                                  onClick={() => handleEditStart(issue)}
                                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors font-medium text-sm flex items-center gap-2"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleIgnore(issue)}
                                  className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 transition-colors font-medium text-sm"
                                >
                                  ✗ Ignore
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No issues found in this analysis
                </p>
              )}
            </div>
          )}

          {!isLoading && !qaResults && (
            <p className="text-gray-500 text-center py-8">
              Click a QA check button to analyze your guide
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {!isLoading && qaResults && qaResults.issues && qaResults.issues.length > 0 && (
              <>
                <span className="font-semibold text-green-700">{acceptedCount} accepted</span>
                <span className="mx-2">•</span>
                <span className="font-semibold text-gray-700">{ignoredCount} ignored</span>
                <span className="mx-2">•</span>
                <span className="font-semibold text-blue-700">{qaResults.issues.length - acceptedCount - ignoredCount} pending</span>
              </>
            )}
          </div>
          <div className="flex gap-3">
            {!isLoading && qaResults && qaResults.issues && qaResults.issues.length > 0 && acceptedCount > 0 && (
              <button
                onClick={handleApplyAll}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                Apply {acceptedCount} Change{acceptedCount !== 1 ? 's' : ''}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QADialog;
